import { delay } from 'redux-saga';
import {
  call,
  all,
  take,
  put,
  race,
  select,
  takeLatest,
  takeEvery,
  getContext,
} from 'redux-saga/effects';
import { IntegrationContext } from 'enums/integration-context';
import jwtDecode from 'jwt-decode';
import { push } from 'connected-react-router';
import { navigateToCMS } from 'utils/UrlOpen';

import OnboardingSession from 'utils/Onboarding';
import SSEHandler from 'utils/SSEHandler';
import { NO_AUTH_LOCATION_PATHNAME } from 'constants';

import { removeConnectionHistoryListItem } from 'reducers/monitor/connect-wizzard/actions';
import { removeCaseIntegration } from 'reducers/cases/actions';
import { removeIntegration as removeMonitorIntegration } from 'reducers/monitor/client/actions';

import { Logger } from '../../utils/Logger';

import {
  LOGIN_REQUEST,
  LOGIN_SUCCESS,
  LOGIN_CALLBACK,
  LOGOUT,
  DEMASQUERADE,
  REQUEST_REFRESH,
  REQUEST_LOGIN_BY_SESSION,
  REQUEST_INTEGRATION_AUTHORIZATION_URL,
  REQUEST_INTEGRATION_AUTHORIZATION_INVITE_URL,
  REMOVE_INTEGRATION_CONNECTED,
  REMOVE_TRADE_HISTORY_INTEGRATION_CONNECTED,
  REDIRECT_TO_AUTH0_LOGIN,
  SIGN_IN,
} from './types';

import { logoutSuccess } from './actions';

import {
  loginSuccess,
  loginFailure,
  refreshTokenInProgress,
  refreshTokenSuccess,
  refreshTokenFailure,
  requestIntegrationAuthorizationUrlInProgress,
  requestIntegrationAuthorizationUrlSuccess,
  requestIntegrationAuthorizationUrlFailure,
  signInSuccess,
  signInFailure,
} from './reducer';

import { makeSelectExpiresAt, makeSelectRefreshing, makeSelectEmail } from './selectors';

const logger = new Logger('Saga:Auth');
const SUCCESS_URL_REDIRECT = '/dashboard';
const FAILURE_URL_REDIRECT = '/login';

/**
 * @name onLoginSuccess
 * @description
 * Method called when an login is successful, being it
 * active or passive. The method will save the session,
 * call the corresponding action and navigate.
 */
export function* onLoginSuccess(session, redirect = false) {
  const sessionService = yield getContext('sessionService');
  const lastPosition = yield JSON.parse(localStorage.getItem(NO_AUTH_LOCATION_PATHNAME));
  const redirectUrl = lastPosition?.expiresAt >= new Date().getTime() && lastPosition?.pathName;

  // Save the session into local storage
  sessionService.set(session);

  logger.info(`Login successful with token \`${session.accessToken}\``);

  yield put(loginSuccess(session));
  yield SSEHandler.openConnection();

  if (redirectUrl) {
    logger.info(`Redirecting after login ${redirectUrl}`);
    yield put(push(redirectUrl));
    // yield localStorage.removeItem(NO_AUTH_LOCATION_PATHNAME);
  } else if (redirect) {
    logger.info(`Redirecting after login to default path ${SUCCESS_URL_REDIRECT}`);
    yield put(push(SUCCESS_URL_REDIRECT));
  }
}

/**
 * @name onLoginFailure
 * @description
 * Method called when an login fails, being it
 * active or passive. The method will save the session,
 * call the corresponding action and navigate.
 */
export function* onLoginFailure(error) {
  logger.error('Login failed with error', error);

  const sessionService = yield getContext('sessionService');

  sessionService.remove();

  yield put(loginFailure(error));
  yield put(push(FAILURE_URL_REDIRECT));
}

/**
 * @name sessionStartupSaga
 * @description
 * Method called on startup to check the session state.
 * If a session is found the loginSuccess method will be called.
 */
export function* sessionStartupSaga(action) {
  logger.info('Restoring session from storage layer...');

  const sessionService = yield getContext('sessionService');
  const session = sessionService.get();

  const redirectUrl = action?.redirectUrl ?? false;

  const lastPosition = JSON.parse(localStorage.getItem(NO_AUTH_LOCATION_PATHNAME));
  const prevRedirectUrl = lastPosition?.expiresAt >= new Date().getTime() && lastPosition?.pathName;

  logger.info(`Redirect url ${prevRedirectUrl}`);

  if (redirectUrl && !prevRedirectUrl) {
    logger.info(`No previous redirect path, setting redirect ${redirectUrl}`);
    localStorage.setItem(
      NO_AUTH_LOCATION_PATHNAME,
      JSON.stringify({
        expiresAt: new Date().getTime() + 5 * 60000,
        pathName: redirectUrl,
      }),
    );
  }

  if (!session) {
    logger.info('No session found');
    return;
  }

  yield call(onLoginSuccess, session, !!redirectUrl);
}

/**
 * @deprecated
 * @name loginRequestSaga
 * @description
 * Method called when a login is requested. This will trigger
 * Auth0 lock and handle the response.
 */
export function* loginRequestSaga() {
  const authService = yield getContext('authService');

  try {
    yield all([
      call(onLoginResponseSaga), // Wait for the Auth0 response
      call(authService.login), // Open the login modal
    ]);
  } catch (error) {
    yield call(onLoginFailure, error);
  }
}

export function* signInRequestSaga(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const { email, password } = action;
    const response = yield authApiService.post(routerService.generate('api.platform.auth_login'), {
      email,
      password,
    });

    const token = yield call(jwtDecode, response.data.access_token);
    yield call(
      onLoginSuccess,
      {
        accessToken: response.data.access_token,
        expiresAt: token.expires_in,
        tokenPayload: token,
      },
      true,
    );
    logger.info('Signed in successfully');
    yield put(signInSuccess());
  } catch (error) {
    const errorMessage = error?.response?.data?.message ?? 'Failed to sign in';
    logger.error('Failed to sign in');
    yield put(signInFailure(errorMessage));
  }
}

/**
 * @name onLoginResponseSaga
 * @description
 * Method that listens to the login event and either calls
 * the onLoginSuccess or the onLoginFailure method based on
 * the response.
 */
export function* onLoginResponseSaga() {
  const authService = yield getContext('authService');

  try {
    const session = yield call(authService.onAuthentication);

    yield call(onLoginSuccess, session, true);
  } catch (error) {
    yield call(onLoginFailure, error);
  }
}

/**
 * @name logoutSaga
 * @description
 * Method that clears the session and redirects the user.
 */
export function* logoutSaga() {
  const sessionService = yield getContext('sessionService');

  OnboardingSession.unskip();
  sessionService.remove();
  SSEHandler.closeConnection();

  if (window._env_.production) {
    return navigateToCMS();
  }

  yield put(push(FAILURE_URL_REDIRECT));
  yield put(logoutSuccess());
}

/**
 * @name logoutSaga
 * @description
 * Method that clears the session and redirects the user.
 */
export function* demasqueradeSaga() {
  const sessionService = yield getContext('sessionService');

  sessionService.demasqueradeFromStorage();

  yield put(push('/demasquerade'));
}

/**
 * @name onRefreshSuccess
 * @description
 * Saga called when the refresh token was successful. It generates a new
 * session object and saves it.
 */
export function* onRefreshSuccess(accessToken) {
  const sessionService = yield getContext('sessionService');
  const payload = sessionService.decodeToken(accessToken);

  // Build a new session object
  const session = { accessToken, expiresAt: payload.exp, tokenPayload: payload };

  // Save the session into local storage
  sessionService.set(session);

  logger.info(`Refreshed token with success with token \`${accessToken}\``);
  yield put(refreshTokenSuccess(session));
}

/**
 * @name onRefreshFailure
 * @description
 * Saga called when the refresh token failed. It propagates the failure event
 */
export function* onRefreshFailure() {
  logger.info('Failed to refresh the token');
  yield put(refreshTokenFailure());
}

/**
 * @name refreshToken
 * @description
 * Method that asks the API for a new access token.
 * This will either trigger the `onRefreshSuccess` or `onRefreshFailure` methods.
 */
export function* refreshTokenSaga(action) {
  if (action?.avoidRefreshing) {
    return;
  }
  // Check if an refresh flow is already running and skips if that's the case
  const isRefreshing = yield select(makeSelectRefreshing());
  if (isRefreshing) {
    return;
  }

  // Send the refresh token in process event and locks the flow to further requests
  yield put(refreshTokenInProgress());

  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  logger.info('Token Renewal process started...');

  try {
    // Call the API to get a new token. We need to use the http client directly to prevent
    // loops if the requests returns 401
    const response = yield call(
      authApiService.client.request,
      routerService.generate('api.refresh_token'),
    );

    yield call(onRefreshSuccess, response.data.token);
  } catch (error) {
    logger.info('Failed to refresh the token with the following error', error);
    yield call(onRefreshFailure);
  }
}

/**
 * @name scheduleTokenRefreshSaga
 * @description
 * Method that checks the `expiresAt` on the session and waits for the required amount of time.
 * If no logout action is triggered in the meantime the renewal process will start.
 */
export function* scheduleTokenRefreshSaga() {
  const expiresAt = yield select(makeSelectExpiresAt());

  if (!expiresAt) {
    return;
  }

  const delayMs = expiresAt * 1000 - Date.now();

  if (delayMs <= 0) {
    return yield call(refreshTokenSaga);
  }

  logger.info(`Scheduling token renewal in ${delayMs}ms.`);
  const { timeout, logout } = yield race({
    timeout: call(delay, delayMs),
    logout: take(LOGOUT),
  });

  if (!timeout && logout) {
    logger.info('Token renewal scheduling cancelled because the user logged out.');
    return;
  }

  return yield call(refreshTokenSaga);
}

export function* requestIntegrationAuthorizationUrlSaga(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');
  yield put(requestIntegrationAuthorizationUrlInProgress());
  const { integration, objectId, redirect } = action;
  const generatedBy = yield select(makeSelectEmail());
  try {
    const response = yield authApiService.post(
      routerService.generate('api.callbacks.connect', {
        provider: integration,
        context: IntegrationContext.client,
      }),
      {
        redirectUri: redirect,
        business: objectId,
        generatedBy,
      },
    );
    action.promise && action.promise.resolve(response.data);
    yield put(requestIntegrationAuthorizationUrlSuccess(response.data.authorizationUrl));
  } catch (error) {
    logger.error('Failed to get integration authorization url with the following error', error);
    action.promise && action.promise.reject(error);
    yield put(requestIntegrationAuthorizationUrlFailure(error));
  }
}

export function* requestIntegrationAuthorizationInviteUrlSaga(action) {
  const routerService = yield getContext('routerService');
  yield put(requestIntegrationAuthorizationUrlInProgress());

  try {
    const response = routerService.generate('api.callbacks.connect', {
      provider: action.integration,
      context: action.context,
    });

    action.promise && action.promise.resolve(response);
    yield put(requestIntegrationAuthorizationUrlSuccess(response.data.authorizationUrl));
  } catch (error) {
    logger.error('Failed to get integration authorization url with the following error', error);
    action.promise && action.promise.reject(error);
    yield put(requestIntegrationAuthorizationUrlFailure(error));
  }
}

export function* removeTradeHistoryIntegrationConnectedSage(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const response = yield authApiService.delete(
      routerService.generate('api.callbacks.disconnect', {
        provider: action.integration,
        objectId: action.objectId,
      }),
      {
        params: {
          legacy: true,
        },
      },
    );

    action.promise && action.promise.resolve(response.data);
    yield put(removeConnectionHistoryListItem(action.objectId));
  } catch (error) {
    logger.error(
      'Failed to remove trade history integration connected with the following error',
      error,
    );
    action.promise && action.promise.reject(error);
  }
}

export function* removeIntegrationConnectedSage(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const response = yield authApiService.delete(
      routerService.generate('api.callbacks.disconnect', {
        provider: action.providerId,
        objectId: action.isFundingCase ? action.caseId : action.businessId,
      }),
      {
        params: {
          legacy: action.isFundingCase,
        },
      },
    );

    action.promise && action.promise.resolve(response.data);
    yield put(removeConnectionHistoryListItem(action.businessId));
    yield put(removeMonitorIntegration(action.businessId));
    yield put(removeCaseIntegration(action.businessId));
  } catch (error) {
    logger.error('Failed to remove integration with the following error', error);
    action.promise && action.promise.reject(error);
  }
}

export function* redirectToAuth0LoginSaga() {
  const auth0SDK = yield getContext('auth0SDK');

  auth0SDK.authorize();
}

export default function* authSaga() {
  return yield all([
    takeLatest(SIGN_IN.request, signInRequestSaga),
    takeLatest(LOGIN_REQUEST, loginRequestSaga),
    takeLatest(LOGIN_CALLBACK, onLoginResponseSaga),
    takeLatest(LOGOUT, logoutSaga),
    takeLatest(LOGIN_SUCCESS, scheduleTokenRefreshSaga),
    takeLatest(DEMASQUERADE, demasqueradeSaga),
    takeLatest(REQUEST_INTEGRATION_AUTHORIZATION_URL, requestIntegrationAuthorizationUrlSaga),
    takeLatest(
      REQUEST_INTEGRATION_AUTHORIZATION_INVITE_URL,
      requestIntegrationAuthorizationInviteUrlSaga,
    ),
    takeLatest(REMOVE_INTEGRATION_CONNECTED, removeIntegrationConnectedSage),
    takeLatest(
      REMOVE_TRADE_HISTORY_INTEGRATION_CONNECTED,
      removeTradeHistoryIntegrationConnectedSage,
    ),
    takeLatest(REDIRECT_TO_AUTH0_LOGIN, redirectToAuth0LoginSaga),
    // REMINDER: This can be replaced by a `takeLeading` and the inProgress lock removed
    // once it is released on redux-saga@1.0.0
    takeEvery(REQUEST_REFRESH, refreshTokenSaga),
    takeLatest(REQUEST_LOGIN_BY_SESSION, sessionStartupSaga),
    sessionStartupSaga(),
  ]);
}
